<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 id="alport-test-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="alport-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <p>
                <a
                  href="https://www.vcgs.org.au/order/tests/1349"
                  target="_blank"
                  >Alport Syndrome</a
                >
                is a genetic disorder which involves progressive loss of kidney
                function and may lead to severe hearing damage and eye
                abnormalities. This kidney related condition is caused by gene
                changes that significantly affect collagen in renal structures
                that play a vital role in filtering waste products from blood to
                create urine. Medicare item number: 73298
              </p>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col class="custom-col">
              <v-alert outlined text color="info" class="pl-10">
                <table>
                  <tr>
                    <td>
                      <b>Eligibility criteria:</b>
                    </td>
                  </tr>
                  <p>
                    Requests must be made by a clinical geneticist or specialist
                    nephrologist and the patient must have been assessed as
                    having relevant clinical and family history strongly
                    suggestive of Alport syndrome.
                  </p>
                  <p>
                    If your patient does not meet this criteria, you can still
                    order and pay for this test by selecting clinical exome
                    sequencing with an analysis type of Exome Focused and select
                    Haematuria_Alport for the gene panel.
                  </p>
                </table>
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical features</span>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert/>
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                dense
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>
        </v-container>

        <AlportConsent
          ref="alportConsent"
          :is-trio="false"
          :force-form-dirty="forceFormDirty"
          :testName="testName"
          :testId="testId"
        ></AlportConsent>

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import GenesMixin from "../../mixins/GenesMixin.vue";
import GenePanelMixin from "../../mixins/GenePanelMixin.vue";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import AlportConsent from "./AlportConsentContainer";
import { EventBus } from "@/event-bus";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceContainer from "../common/TestPriceContainer.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.alportTestPricing = await this.execRetrieveJson(
      "json/pricing/alport_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");

    const element = document.getElementById("alport-test-title");

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    this.urgency = "Standard";
    this.familyType = "Singleton";
    this.analysisType = "Exome Focused";

    const pricing = this.alportTestPricing[this.urgency][this.familyType][
      this.analysisType
    ];

    this.price = pricing.price;
    this.testCode = pricing.testCode;
    this.medicare = pricing.medicare;

    this.$emit("toggleTotalPrice", this.price, this.testId);
    EventBus.$emit("medicareAlportStatus", true);
  },
  mixins: [
    FormRulesMixin,
    PhenotypesMixin,
    RetrieveJSONMixin,
    GenePanelMixin,
    GenesMixin,
  ],
  name: "",
  components: { AlportConsent, TestPriceContainer, BrokenApiAlert, BadSearchQueryAlert },
  data: () => ({
    valid: false,
    urgent: false,
    urgentReason: "",
    alportTestPricing: null,
    cgApproved: true,
    cgDetails: "",
    genePanels: ["Haematuria_Alport"],
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    ageOfOnset: "",
    urgency: null,
    familyType: null,
    analysisType: null,
    indGenes: [],
    billTo: "MEDICARE",
    searchInputGenes: null,
    searchInputPhenotypes: null,
    price: null,
    testCode: null,
    medicare: true,
  }),
  methods: {
    isFormValid: function() {
      if (typeof this.$refs.alportConsent !== "undefined") {
        return this.$refs.alportConsent.isFormValid();
      } else {
        return false;
      }
    },

    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];

      for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
        if (familyComponent && familyKey.substring(0, 12) === "familyMember") {
          familyDetails.push(familyComponent.transferData());
        }
      }

      // Convert hardcoded gene panels value to "<panelId> - <panelName>|<panelId> - <panelName>|..."
      const finalGenePanels =
        this.genePanels[0] === "Haematuria_Alport"
          ? "39 - Haematuria_Alport"
          : "";

      let result = {
        urgent: this.urgent,
        urgency: this.urgency,
        urgentReason: "",
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        rapid: this.rapidTest,
        genePanels: finalGenePanels,
        indGenes: this.getFinalIndGenes(this.indGenes),
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        ageOfOnset: this.ageOfOnset,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        cgApproved: this.cgApproved,
        cgDetails: this.cgDetails,
        billTo: this.billTo,
      };

      // Consent data is present, except if the test is medicare but not approved by gc
      if (this.$refs.alportConsent) {
        const consentData = this.$refs.alportConsent.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
